/**
 * <DonateInvitation /> — friendly, non-intrusive reminder inviting free
 * users to support the platform via a donation or upgrade.
 *
 * Visible on /dashboard only for users who:
 *   • don't have an active premium subscription, AND
 *   • have dismissed neither the last week (localStorage cooldown).
 *
 * We keep the tone warm and community-first, not pushy.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Heart, Sparkles, X } from "lucide-react";

const DISMISS_KEY = "sp:donate-invite-dismissed-until";

function isDismissedNow() {
  try {
    const iso = localStorage.getItem(DISMISS_KEY);
    if (!iso) return false;
    return new Date(iso).getTime() > Date.now();
  } catch { return false; }
}

export default function DonateInvitation() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(isDismissedNow);

  useEffect(() => {
    if (dismissed) return undefined;
    let mounted = true;
    (async () => {
      try {
        const { data } = await http.get("/premium/me");
        // Show only to free (non-premium) users.
        if (mounted && !data?.is_premium) setVisible(true);
      } catch { /* silent — assume free */ if (mounted) setVisible(true); }
    })();
    return () => { mounted = false; };
  }, [dismissed]);

  const dismiss = () => {
    const week = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    try { localStorage.setItem(DISMISS_KEY, week); } catch { /* ignore */ }
    setDismissed(true);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="donate-invitation"
      className="wofy-card relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #FEF3C7 0%, #FFE4E6 100%)", borderColor: "var(--wofy-primary)" }}
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 opacity-50 hover:opacity-100"
        aria-label="Dismiss for a week"
        data-testid="donate-invitation-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--wofy-primary)" }}>
          <Heart className="w-5 h-5 text-white fill-white" />
        </div>
        <div className="flex-1">
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>SUPPORT SOCIAL PULSE</div>
          <h2 className="font-display text-xl">Everything here is free. Always.</h2>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text)" }}>
            Every feature — causes, memorials, marketplace, family tree, news — is free for you.
            If Social Pulse has added value to your day, a small contribution keeps it ad-light
            and community-owned. <b>Zero pressure.</b>
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/donate" className="wofy-btn-primary text-sm" data-testid="donate-invitation-donate">
              <Heart className="w-4 h-4" /> Donate
            </Link>
            <Link to="/pricing" className="wofy-btn-ghost text-sm border" style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" }} data-testid="donate-invitation-pricing">
              <Sparkles className="w-4 h-4" /> See premium
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
